import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileUpload } from '@/components/ui/file-upload';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { apiErrorMessage, useRequireApiDocsManager, useUploadDocument } from '../api';
import { DOCUMENT_TYPES } from '../types';

const LIST = '/superadmin-api-docs/upload-document';
const MAX_BYTES = 100 * 1024 * 1024;

export function DocumentAddPage() {
  useRequireApiDocsManager();
  const navigate = useNavigate();
  const upload = useUploadDocument();
  const [name, setName] = useState('');
  const [type, setType] = useState('SIMPLE_DOCUMENT');
  const [files, setFiles] = useState<File[]>([]);

  const submit = () => {
    if (!name.trim()) return toast.error('Please enter a document name');
    const file = files[0];
    if (!file) return toast.error('Please select or drop a file to upload');
    upload.mutate(
      { file, documentName: name.trim(), documentType: type },
      {
        onSuccess: (r) => {
          toast.success(r.message);
          navigate(LIST);
        },
        onError: (e) => toast.error(apiErrorMessage(e, 'Upload failed')),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Upload Document"
        description="Accepts .pdf, .doc, .docx, .xls, .xlsx, .csv or .txt up to 100 MB."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(LIST)} disabled={upload.isPending}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={upload.isPending}>
              {upload.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Upload
            </Button>
          </div>
        }
      />
      <div className="grid max-w-2xl gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="docName">Document Name</Label>
          <Input id="docName" value={name} onChange={(e) => setName(e.target.value)} maxLength={255} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="docType">Document Type</Label>
          <select
            id="docType"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            {DOCUMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <FileUpload
          value={files}
          onChange={setFiles}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
          maxSize={MAX_BYTES}
        />
      </div>
    </FillPage>
  );
}
